import type { CityConfig } from "../city-template/types";

export const parkersburgConfig: CityConfig = {
  city: "Parkersburg",
  state: "West Virginia",
  stateAbbr: "WV",
  slug: "parkersburg",
  route: "/parkersburg",
  title: "Emergency Passport Parkersburg, WV — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Parkersburg, WV — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Wood County families, WVU-P students, and Mid-Ohio Valley travelers.",
  h1: "Emergency & Expedited Passport Services in Parkersburg, WV",
  heroSubtitle: "Family cruise departing from Florida in two weeks? DuPont/Chemours international training in Germany? WVU-Parkersburg student exchange program in Costa Rica? We help Parkersburg residents — from downtown historic district families to Vienna professionals to Mineral Wells commuters — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20aerial%20view%20of%20Parkersburg%20West%20Virginia%20along%20the%20Ohio%20River%20with%20the%20historic%20downtown%20district%20and%20Blennerhassett%20Island%20in%20the%20background%2C%20warm%20golden%20hour%20light%20reflecting%20off%20the%20river%2C%20tree-lined%20riverfront%20with%20Victorian%20architecture%20visible%2C%20autumn%20colors%20on%20surrounding%20hills%2C%20peaceful%20Mid-Ohio%20Valley%20landscape%20at%20sunset&width=1800&height=700&seq=parkersburg-hero-2026&orientation=landscape",
  heroImageAlt: "Parkersburg West Virginia along the Ohio River with Blennerhassett Island at golden hour",
  geoPosition: "39.2667;-81.5615",
  geoPlacename: "Parkersburg, West Virginia",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Washington Passport Agency",
    address: "600 19th St NW, Washington, DC 20006",
    distance: "Approximately 320 miles from Parkersburg — appointments required, typically booked 2+ weeks out",
  },
  nearbyCitiesZips: [
    { name: "Parkersburg", zips: "26101, 26102, 26103, 26104, 26105" },
    { name: "Vienna", zips: "26105" },
    { name: "Mineral Wells", zips: "26150" },
    { name: "Williamstown", zips: "26187" },
    { name: "Marietta, OH", zips: "45750" },
    { name: "Belpre, OH", zips: "45714" },
    { name: "Ripley", zips: "25271" },
    { name: "Ravenswood", zips: "26164" },
  ],
  benefits: [
    { icon: "ri-ship-line", title: "Ohio River Valley Expertise", desc: "The Mid-Ohio Valley is home to chemical, polymer, and manufacturing industries with international supply chains. We understand the business travel patterns of Parkersburg's industrial base and the timelines corporate travelers need." },
    { icon: "ri-flight-takeoff-line", title: "Regional Airport Coordination", desc: "Parkersburg travelers use multiple airports — PKB for connections, CMH (Columbus) 115 miles, and PIT (Pittsburgh) 130 miles. We build your specific airport timeline into the passport processing plan." },
    { icon: "ri-building-2-line", title: "Chemical Industry Travel", desc: "DuPont/Chemours, Kraton Polymers, and other Mid-Ohio Valley employers send engineers and managers to international facilities, conferences, and training. Short-notice travel is common — we are built for it." },
    { icon: "ri-graduation-cap-line", title: "WVU-P & College Travel", desc: "WVU-Parkersburg students studying abroad, Ohio Valley University travelers, and Marietta College students across the river all benefit from our student-focused expedited passport guidance." },
  ],
  testimonials: [
    {
      name: "Robert T.",
      location: "Vienna, WV",
      rating: 5,
      text: "Chemours sent me to a polymer conference in Frankfurt with 9 days notice. My passport had expired. Fast Passport Center got it renewed in 5 days. My colleagues who used the post office were still waiting when I returned.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "The Williams Family",
      location: "Mineral Wells",
      rating: 5,
      text: "Family cruise out of Fort Lauderdale booked six months ago, but my daughter needed a first-time passport. They walked us through the acceptance visit at the Parkersburg post office and we had her passport in hand 8 days later.",
      service: "Child Passport — First Time",
    },
    {
      name: "Angela R.",
      location: "Downtown Parkersburg",
      rating: 5,
      text: "WVU-P study abroad to Costa Rica — I had never traveled internationally. The checklist was clear, acceptance visit instructions were perfect, and my passport arrived with time to spare before orientation.",
      service: "New Passport",
    },
    {
      name: "Jim H.",
      location: "Williamstown",
      rating: 5,
      text: "Business trip to Shanghai for polymer supply chain meetings. Needed a second passport since my primary was in visa processing. They handled it professionally and I had both passports in time for my trip.",
      service: "Second Passport",
    },
  ],
  facilities: [
    {
      id: "parkersburg-1",
      name: "Parkersburg Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "204 5th St",
      city: "Parkersburg",
      state: "WV",
      zip: "26101",
      phone: "(304) 422-6311",
      hours: "Mon–Fri 9:00 AM – 3:00 PM (appointment required)",
      distance: "Downtown Parkersburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Primary acceptance facility for Parkersburg and Wood County. Passport photos available on-site. Appointment required — limited daily slots, schedule at least a week in advance during peak seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "parkersburg-2",
      name: "Wood County Circuit Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2 Government Square, Room 133",
      city: "Parkersburg",
      state: "WV",
      zip: "26101",
      phone: "(304) 424-1860",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      distance: "Downtown Parkersburg — Courthouse",
      website: "https://www.woodcountywv.com/county-government/circuit-clerk",
      websiteLabel: "Wood County Clerk",
      notes: "County clerk office accepting passport applications. Located in the Wood County Courthouse. Often less crowded than the post office. No photo services on-site.",
      icon: "ri-government-line",
    },
    {
      id: "parkersburg-3",
      name: "Marietta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "301 Greene St",
      city: "Marietta",
      state: "OH",
      zip: "45750",
      phone: "(740) 373-1154",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Marietta, OH — across the Ohio River",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Alternative facility for Parkersburg-area residents willing to cross the Ohio River. Serves Washington County, OH. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "parkersburg-4",
      name: "Vienna Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1500 Grand Central Ave",
      city: "Vienna",
      state: "WV",
      zip: "26105",
      phone: "(304) 295-9222",
      hours: "Mon–Fri 9:00 AM – 3:00 PM",
      distance: "Vienna — 3 miles north of Parkersburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Serving northern Wood County and Vienna residents. Smaller facility with limited appointment availability — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "parkersburg-5",
      name: "Ripley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "114 Church St S",
      city: "Ripley",
      state: "WV",
      zip: "25271",
      phone: "(304) 372-6272",
      hours: "Mon–Fri 9:00 AM – 1:00 PM",
      distance: "Ripley — 25 miles south, Jackson County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Serving Jackson County and southern Wood County residents. Limited morning hours only — call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    { q: "How fast can I get a passport in Parkersburg?", a: "We offer Parkersburg-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. There is no passport agency anywhere near Parkersburg — the nearest is Washington, DC, 320 miles away. Our courier service eliminates the need for a DC trip entirely." },
    { q: "Do I need to visit an office in Parkersburg?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (Parkersburg Main Post Office or Wood County Clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Parkersburg address." },
    { q: "Which airport should Parkersburg travelers use?", a: "Mid-Ohio Valley Regional Airport (PKB) in Williamstown offers limited commercial flights — primarily connections through Charlotte. For international flights, most Parkersburg travelers use Port Columbus International (CMH, ~115 miles), Pittsburgh International (PIT, ~130 miles), or Charleston Yeager (CRW, ~80 miles). We build your specific airport into the timeline." },
    { q: "What is the best price for expedited passport service in Parkersburg?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "Where can I get a passport photo in Parkersburg?", a: "Walgreens on Emerson Avenue, CVS Pharmacy on Grand Central Avenue, Walmart on Pike Street, and the Parkersburg Main Post Office all offer passport photos. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
    { q: "What documents do Parkersburg first-time applicants need?", a: "Certified birth certificate, government-issued photo ID (WV driver's license or state ID), completed DS-11 form, and passport photo. If you were born in another state, you can order a certified birth certificate through WV DHHR Vital Registration. We provide a personalized checklist." },
    { q: "Does WVU-Parkersburg offer passport services?", a: "WVU-Parkersburg does not have a dedicated passport office, but the Parkersburg Main Post Office at 204 5th Street and the Wood County Clerk at the courthouse both serve as acceptance facilities. Both are within a short drive of the WVU-P campus." },
    { q: "Can I use the Marietta post office across the river?", a: "Yes — the Marietta Post Office (301 Greene St, Marietta, OH) accepts passport applications and serves Washington County, Ohio residents. Parkersburg residents are welcome to use this facility, though you will need to cross the Ohio River bridge. Appointments are required." },
    { q: "What about business travel for chemical industry employees?", a: "We regularly assist Parkersburg-area chemical and polymer industry employees — from DuPont/Chemours to Kraton to smaller suppliers — with expedited passports for international training, conferences, supplier visits, and facility tours. Short-notice travel (1–2 weeks) is common in this sector." },
    { q: "How do I check my passport status from Parkersburg?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system. Processing from submission to final return shipping typically matches your chosen speed tier." },
  ],
  photoLocations: [
    { name: "Walgreens — Emerson Ave", address: "2500 Emerson Ave, Parkersburg, WV 26101", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS Pharmacy — Grand Central Ave", address: "1500 Grand Central Ave, Vienna, WV 26105", hours: "Daily 8 AM – 9 PM" },
    { name: "Walmart Supercenter — Pike St", address: "701 Pike St, Parkersburg, WV 26101", hours: "Daily 6 AM – 11 PM" },
    { name: "Parkersburg Main Post Office", address: "204 5th St, Parkersburg, WV 26101", hours: "Mon–Fri 9 AM – 3 PM" },
  ],
  dmvLocations: [
    {
      name: "WV DMV — Parkersburg Regional Office",
      address: "401 5th St, Parkersburg, WV 26101",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, state ID cards. Appointment recommended.",
      featured: true,
    },
  ],
  dmvWebsite: "https://transportation.wv.gov/dmv/",
  dmvLabel: "West Virginia DMV — Official Website",
  airports: [
    {
      code: "PKB",
      name: "Mid-Ohio Valley Regional Airport",
      distance: "About 8 miles northeast of Parkersburg",
      address: "543 Co Rte 31/1, Williamstown, WV 26187",
      note: "Regional airport with limited commercial service. No international flights — connection point to Charlotte (CLT) via Contour Airlines. Primarily used for domestic connections to international gateways.",
      flightUrl: "https://www.flypkb.com/",
      flightLabel: "PKB Airport Website",
      featured: true,
    },
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "About 115 miles northwest of Parkersburg",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Major regional airport with international service to Cancun, Toronto, and seasonal European destinations. About 2 hours drive via US-50 and US-33. Often the best balance of flight options and drive time.",
      flightUrl: "https://flycolumbus.com/",
      flightLabel: "CMH Airport Website",
      featured: false,
    },
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "About 130 miles northeast of Parkersburg",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "Major international hub with direct flights to London, Paris, Frankfurt. About 2.5 hours drive via I-77 and I-70. Best option for European and long-haul international travel.",
      flightUrl: "https://flypittsburgh.com/",
      flightLabel: "PIT Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate — WV DHHR",
      desc: "Request certified birth certificates from the West Virginia DHHR Vital Registration Office. Online, mail, or in-person options available for Wood County residents.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate — Wood County",
      desc: "Request marriage license records from the Wood County Clerk. Certified copies available for name changes and passport applications.",
      url: "https://www.woodcountywv.com/county-government/county-clerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree — Wood County",
      desc: "Obtain certified divorce records through the Wood County Circuit Clerk. Required for name change passport corrections.",
      url: "https://www.woodcountywv.com/county-government/circuit-clerk",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate — WV DHHR",
      desc: "Obtain certified death certificates through WV DHHR Vital Registration for estate and legal purposes.",
      url: "https://dhhr.wv.gov/bph/hsc/vital/Pages/default.aspx",
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Chemical industry training in Germany next week? Family emergency overseas? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for Parkersburg-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Parkersburg residents renew fast — no agency visit required. Perfect for Vienna families preparing for cruises or Williamstown professionals with international business trips.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport — First Time",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Parkersburg residents through every step with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms and DS-64. We guide you through everything to avoid costly mistakes — so your family cruise or business trip stays on track.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear checklists so your family vacation or cruise departure stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or legal name change — we assist with corrected passports so your travel documents match reservations and IDs.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for chemical industry executives and consultants traveling while a primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Parkersburg-area travelers through the process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "Wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. We expedite passport card applications with the same courier service.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Parkersburg WV", description: "Urgent passport processing for Parkersburg West Virginia travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Parkersburg WV", description: "Fast passport renewal for Parkersburg West Virginia residents." },
    { name: "First-Time Passport Parkersburg WV", description: "New passport application services for Parkersburg West Virginia residents applying for their first passport." },
    { name: "Child Passport Parkersburg WV", description: "Minor passport services for Parkersburg West Virginia families with children under 16." },
    { name: "Name Change Passport Parkersburg WV", description: "Passport name correction after marriage, divorce, or legal name changes for Parkersburg residents." },
    { name: "Second Passport Parkersburg WV", description: "Second passport services for Parkersburg corporate and international travelers." },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Not Planning for Multi-Airport Travel",
      description: "Parkersburg travelers often need to figure out which airport to use — PKB, Columbus, Pittsburgh, or Charleston — and factor drive time into the passport timeline. International flights require arriving 3 hours early plus 2+ hours driving. We build this into your plan.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Cruise Passport Panic",
      description: "Many Mid-Ohio Valley families book cruises departing from Florida ports without realizing passports are required for all passengers — including children. Cruise bookings are often made 6+ months in advance, but passport checks happen last-minute. We expedite so the vacation stays on track.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Photos from Parkersburg-area drugstores and big-box retailers frequently fail State Department standards — background not pure white, shadows on face, glasses not removed. We catch these issues during document review before they cause rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Birth Certificate Confusion for Ohio-Born Residents",
      description: "Many Parkersburg-area residents were born in Ohio hospitals (Marietta, Belpre). When ordering birth certificates for passport applications, you need Ohio vital records — not West Virginia. We guide you to the correct state's system.",
    },
    {
      icon: "ri-error-warning-line",
      title: "DS-82 When DS-11 Is Required",
      description: "Applicants often attempt the simpler renewal form (DS-82) when their passport was issued at age 15 or younger, or expired more than 5 years ago. A rejected form costs 1–2 weeks. We review before submission.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order — takes just a few minutes from home or office." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, including guidance on your chosen departure airport." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using overnight shipping. Most Parkersburg clients use the downtown UPS Store or FedEx Office." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — no need to drive 320 miles to Washington, DC." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport arrives at your Parkersburg address within your selected timeframe. No DC drive, no guesswork." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent", "Updates only by physical mail", "Can't upgrade speed after submission"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — nearest agency is Washington DC (320 miles)", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — nearest is Washington DC, 320 miles away. Typically booked 2+ weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive 320 miles to Washington DC Passport Agency", fpc: "Most Parkersburg travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability" },
    { feature: "Local Guidance", agency: "Generic — no Mid-Ohio Valley, cruise, or industrial travel expertise", fpc: "Full Parkersburg/cruise/chemical industry knowledge" },
    { feature: "Cost vs Resellers", agency: "Government fees only — you handle all logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Fast passport services in Charleston, WV", route: "/charleston" },
    { name: "Expedited passport in Huntington, WV", route: "/huntington" },
    { name: "Quick passport services in Morgantown, WV", route: "/morgantown" },
    { name: "West Virginia passport services overview", route: "/west-virginia" },
  ],
  fpcOfficeAddress: "7272 Wisconsin Ave, Bethesda, Maryland 20814",
  fpcOfficeNote: "Fast Passport Center serves Parkersburg via secure registered courier with direct access to the Washington, DC Passport Agency. Our registered couriers hand-deliver your documents — meaning you get faster results without driving 320 miles to DC. Parkersburg residents can ship from any UPS Store, FedEx Office, or post office location across Wood County.",
  fpcOfficeDistance: "Serving Parkersburg, Vienna, Mineral Wells, Williamstown, and all Wood County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Parkersburg and the greater Mid-Ohio Valley. From the downtown historic district to Vienna, from Mineral Wells to Williamstown, and including Belpre and Marietta, Ohio — we serve all of Wood County and the surrounding Ohio River Valley. Our courier service eliminates the need to drive to Washington, DC.",
  nearbyCitiesIntro: "We serve Parkersburg and all surrounding communities including Vienna, Mineral Wells, Williamstown, Belpre (OH), Marietta (OH), Ripley, and Ravenswood. We also serve travelers departing from PKB, Columbus, Pittsburgh, and Charleston airports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Parkersburg as simple as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Parkersburg travelers start online and ship documents overnight.",
  mistakesIntro: "Parkersburg travelers encounter unique passport issues — from multi-airport planning to cruise document timing to cross-state vital records. Here are the most common mistakes we see.",
  testimonialsIntro: "Parkersburg travelers — from chemical industry professionals to WVU-P students to Mid-Ohio Valley families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Parkersburg, WV? Here are the most common ones we hear from Mid-Ohio Valley travelers.",
  statePageRoute: "/states/west-virginia",
  statePageName: "West Virginia",
  servicesIntro: "Whether you need a renewal before a family cruise, an emergency passport for chemical industry training, or a first-time passport for a WVU-P study abroad program — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price.",
  travelTiming: {
    title: "Travel & Passport Timing for Parkersburg Residents",
    intro: "Parkersburg's location in the Mid-Ohio Valley means travelers must plan around regional airports and drive times. Here's what Parkersburg travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PKB — Mid-Ohio Valley Regional Airport",
        subtitle: "Regional connector airport, 8 miles northeast",
        description: "PKB offers limited commercial service via Contour Airlines to Charlotte (CLT), a major American Airlines hub with connections to international destinations. While PKB itself has no international flights, it is the most convenient starting point for domestic connections to international gateways. Flight frequency is limited — typically 2–3 daily departures.",
        tip: "PKB flights to Charlotte connect to international departures, but missed connections are common with limited daily flights. Build extra buffer time into both your passport processing and your travel itinerary.",
      },
      {
        icon: "ri-ship-line",
        title: "Cruise Season in the Mid-Ohio Valley",
        subtitle: "Family cruises from Florida ports are the #1 passport urgency driver",
        description: "Parkersburg families overwhelmingly choose cruises for international vacations — Carnival, Royal Caribbean, and Norwegian cruise lines departing from Fort Lauderdale, Miami, Port Canaveral, and Tampa. Cruises are typically booked 6–12 months in advance, but passport preparation often happens in the final 3–4 weeks. Spring break (March) and summer (June–July) are peak cruise seasons.",
        tip: "Children need passports for cruises too — even infants. Many Parkersburg families discover this during the final packing week. Start passport applications for the whole family at least 8 weeks before your cruise departure.",
      },
      {
        icon: "ri-building-2-line",
        title: "Chemical & Polymer Industry Travel",
        subtitle: "DuPont/Chemours, Kraton, and industrial suppliers",
        description: "The Parkersburg-Marietta corridor is a historic center of chemical and polymer manufacturing. Engineers, plant managers, and quality assurance professionals regularly travel to international facilities in Germany, China, Japan, Brazil, and India. Travel is often booked on short notice — 1–3 weeks — for training, troubleshooting, and supplier qualification visits.",
        tip: "Industrial travel is frequently booked by corporate travel desks that don't check passport validity. Self-check your expiration date regularly — many business travelers discover expired passports only when packing.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "The Multi-Airport Reality",
      description: "No single airport serves all Parkersburg international travel needs. PKB connects to Charlotte for domestic links, Columbus (CMH, 115 miles) offers the best balance of flights and drive time, Pittsburgh (PIT, 130 miles) has the most international direct flights, and Charleston (CRW, 80 miles) is the closest option. We build your specific airport choice into your passport timeline.",
    },
  },
  destinations: {
    title: "Where Parkersburg Travelers Go",
    intro: "Parkersburg's industrial base and family-oriented community creates distinct international travel patterns. Here are the most common destinations.",
    stats: [
      { stat: "55%", label: "of Parkersburg expedited requests are family cruise or vacation-related" },
      { stat: "2–3 Weeks", label: "average lead time from booking to departure for Parkersburg travelers" },
      { stat: "Jan–Apr", label: "peak season with spring break cruises and Q1 industrial travel colliding" },
      { stat: "4 Days", label: "shortest turnaround for a Chemours engineer departing to Germany" },
    ],
    destinations: [
      { name: "Caribbean & Bahamas", context: "Cruise departures from Florida ports, all-inclusive resorts in Cancun, Punta Cana, and Jamaica", urgency: "Spring break bookings peak January–February", icon: "ri-map-pin-2-line" },
      { name: "Germany", context: "Chemical industry training at BASF, Bayer, and supplier facilities; polymer engineering conferences", urgency: "Industrial travel often booked 1–2 weeks out", icon: "ri-map-pin-2-line" },
      { name: "Japan & China", context: "Chemical supply chain visits, WVU-P exchange programs, polymer industry trade shows", urgency: "Visa requirements add extra weeks beyond passport processing", icon: "ri-map-pin-2-line" },
      { name: "United Kingdom", context: "PIT-London direct flight, industrial standards conferences, family heritage travel", urgency: "Business meetings with fixed dates booked 2–3 weeks out", icon: "ri-map-pin-2-line" },
      { name: "Mexico", context: "All-inclusive resort vacations, Cancun and Riviera Maya, family spring break trips", urgency: "Last-minute resort deals booked 3–4 weeks before departure", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Niagara Falls family trips, Toronto business travel, industrial supplier visits in Ontario", urgency: "Cross-border business bookings often 1–2 weeks out", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other West Virginia Service Areas",
    crossLinksDesc: "We serve travelers across the Mountain State.",
    crossLinks: [
      { name: "Charleston", route: "/charleston" },
      { name: "Huntington", route: "/huntington" },
    ],
  },
};